/** @const {string} */
export const CONNECTION_ERROR_MESSAGE =
  "We couldn't reach Orderlink. Check your connection and try again.";

/** @const {string} */
export const SERVER_ERROR_MESSAGE =
  "Something went wrong on our side. Your changes were not saved — please try again.";

/**
 * Turns an API field name into words a person would use.
 * "from_warehouse_id" → "From warehouse".
 *
 * @param {string} key
 * @return {string}
 */
export const humaniseFieldName = key => {
  const words = String(key)
    .replace(/_ids?$/, "")
    .replace(/[_-]+/g, " ")
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : String(key);
};

/**
 * Message for failures where the server never answered or crashed, or null when
 * the server returned a normal validation error.
 *
 * @param {*} error RTK Query error.
 * @return {?string}
 */
export const transportErrorMessage = error => {
  const status = error?.status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return CONNECTION_ERROR_MESSAGE;
  }
  if (typeof status === "number" && status >= 500) {
    return SERVER_ERROR_MESSAGE;
  }
  return null;
};

/**
 * Extract a human-readable error message from a typical RTK Query / DRF error.
 *
 * @param {*} error Error object from RTK Query unwrap catch.
 * @param {string} fallback Message when nothing else is found.
 * @return {string}
 */
export const getApiErrorMessage = (error, fallback = "Something went wrong.") => {
  const transport = transportErrorMessage(error);
  if (transport) {
    return transport;
  }
  const data = error?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data.detail === "string") {
    return data.detail;
  }
  if (Array.isArray(data.detail)) {
    return data.detail.map(item => (typeof item === "string" ? item : JSON.stringify(item))).join(" ");
  }
  if (typeof data.message === "string") {
    return data.message;
  }
  if (typeof data.status === "string") {
    return data.status;
  }
  if (typeof data === "object") {
    const parts = [];
    Object.entries(data).forEach(([key, value]) => {
      const text = Array.isArray(value)
        ? value.filter(item => typeof item === "string").join(" ")
        : typeof value === "string"
          ? value
          : "";
      if (!text) return;
      parts.push(key === "non_field_errors" ? text : `${humaniseFieldName(key)}: ${text}`);
    });
    if (parts.length) {
      return parts.join(" ");
    }
  }
  return fallback;
};

/**
 * @param {unknown} value
 * @return {string|undefined}
 */
const firstStringMessage = value => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (typeof first === "string") {
      return first;
    }
  }
  return undefined;
};

/**
 * @param {Record<string, unknown>} rowErrors
 * @param {Record<string, string>} fieldLabels
 * @return {string|undefined}
 */
const formatRowErrors = (rowErrors, fieldLabels) => {
  if (!rowErrors || typeof rowErrors !== "object" || Array.isArray(rowErrors)) {
    return undefined;
  }
  const keys = Object.keys(rowErrors);
  if (keys.length === 0) {
    return undefined;
  }
  for (const key of keys) {
    const message = firstStringMessage(rowErrors[key]);
    if (message) {
      const label = fieldLabels[key] || humaniseFieldName(key);
      return `${label}: ${message}`;
    }
  }
  return undefined;
};

/**
 * Formats DRF / RTK Query error payloads for toast display, including line-level
 * errors with row numbers.
 *
 * @param {unknown} error
 * @param {{
 *   fallback?: string,
 *   listLabels?: Record<string, string>,
 *   fieldLabels?: Record<string, string>,
 *   rowNumbers?: Record<string, number[]>,
 * }} [options]
 * @return {string}
 */
export const formatApiErrorMessage = (error, options = {}) => {
  const {
    fallback = "Something went wrong",
    listLabels = {},
    fieldLabels = {},
    rowNumbers = {},
  } = options;

  const transport = transportErrorMessage(error);
  if (transport) {
    return transport;
  }

  const data = error?.data;
  if (!data || typeof data !== "object") {
    return fallback;
  }

  const messages = [];

  const detail = firstStringMessage(data.detail);
  if (detail) {
    messages.push(detail);
  }

  const nonField = firstStringMessage(data.non_field_errors);
  if (nonField) {
    messages.push(nonField);
  }

  Object.keys(data).forEach(key => {
    if (key === "detail" || key === "non_field_errors") {
      return;
    }
    const value = data[key];

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const listLabel = listLabels[key] || key;
      const rowIndexMap = rowNumbers[key] || [];
      value.forEach((rowErrors, payloadIndex) => {
        const rowMessage = formatRowErrors(rowErrors, fieldLabels);
        if (!rowMessage) {
          return;
        }
        const displayRow =
          rowIndexMap[payloadIndex] !== undefined ? rowIndexMap[payloadIndex] : payloadIndex + 1;
        messages.push(`${listLabel} ${displayRow} - ${rowMessage}`);
      });
      return;
    }

    const fieldMessage = firstStringMessage(value);
    if (fieldMessage) {
      const label = fieldLabels[key] || humaniseFieldName(key);
      messages.push(`${label}: ${fieldMessage}`);
    }
  });

  if (messages.length === 0) {
    return fallback;
  }

  return messages.slice(0, 3).join("\n");
};
